import React from 'react';
import { useFormContext } from 'react-hook-form';

import {
  trackApplePaySelectionInGTM,
  trackGooglePaySelectionInGTM,
} from '@/analytics/gtm';
import { useIsUpdatingBookingAddOns } from '@/booking-add-ons';
import { PaymentTime } from '@/booking/interfaces/PaymentTime.interface';
import { AdditionalInfoFormFields } from '@/booking/validators/additional-info-form.validator';
import { Box, Flex } from '@/box';
import type { TFunction } from '@/i18n';
import { useTranslation } from '@/i18n';
import { ApplePayButton, Button, GooglePayButton } from '@/ui/controls';
import { useSpaceMatchingKey, useTheme } from '@/ui/theme/hooks';

const getCardPaymentButtonCopy = (
  paymentTime: PaymentTime,
  isOneClickPaymentProviderAvailable: boolean,
  t: TFunction<('additionalInformation' | 'common')[]>
) => {
  if (!isOneClickPaymentProviderAvailable) return t('common:controls.continue');

  return paymentTime === PaymentTime.PayLater
    ? t('additionalInformation:buttons.bookWithCard')
    : t('additionalInformation:buttons.payWithCard');
};

interface PaymentMethodButtonsProps {
  availableOneClickPaymentProvider: 'applePay' | 'googlePay' | null;
  paymentTime: PaymentTime;
  onSubmit: (
    values: AdditionalInfoFormFields,
    hasRequestedOneClickCheckout: boolean
  ) => void;
  className?: string;
}

export const PaymentMethodButtons = (props: PaymentMethodButtonsProps) => {
  const { t } = useTranslation(['additionalInformation', 'common']);
  const { forms } = useTheme();
  const oneClickPayButtonType =
    props.paymentTime === PaymentTime.PayNow ? 'pay' : 'book';

  const isUpdatingAddOns = useIsUpdatingBookingAddOns();

  const xs = useSpaceMatchingKey('xxs');

  const { handleSubmit } = useFormContext<AdditionalInfoFormFields>();

  return (
    <Flex flexDirection={['column', 'row']} className={props.className}>
      {props.availableOneClickPaymentProvider === 'applePay' && (
        <Box flex="1" marginInlineEnd={[0, xs]} marginBottom={[xs, 0]}>
          <ApplePayButton
            onClick={handleSubmit((values) => {
              trackApplePaySelectionInGTM();
              props.onSubmit(values, true);
            })}
            isDisabled={isUpdatingAddOns}
            type={oneClickPayButtonType}
          />
        </Box>
      )}
      {props.availableOneClickPaymentProvider === 'googlePay' && (
        <Box flex="1" marginInlineEnd={[0, xs]} marginBottom={[xs, 0]}>
          <GooglePayButton
            onClick={handleSubmit((values) => {
              trackGooglePaySelectionInGTM();
              props.onSubmit(values, true);
            })}
            isDisabled={isUpdatingAddOns}
            type={oneClickPayButtonType}
          />
        </Box>
      )}

      <Box
        flex="1"
        marginInlineStart={[0, props.availableOneClickPaymentProvider ? xs : 0]}
      >
        <Button
          onClick={handleSubmit((values) => {
            props.onSubmit(values, false);
          })}
          isDisabled={isUpdatingAddOns}
          css={forms.checkoutForm?.paymentButton}
        >
          {getCardPaymentButtonCopy(
            props.paymentTime,
            props.availableOneClickPaymentProvider === 'applePay' ||
              props.availableOneClickPaymentProvider === 'googlePay',
            t
          )}
        </Button>
      </Box>
    </Flex>
  );
};

export default PaymentMethodButtons;
